import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useChatSocket } from 'src/app/pages/messages/context/useChatSocket';

const IDLE_MS = 60 * 1000; // 60 sn giriş yoksa "boşta"

/**
 * useActivityTracker — giriş yapmış her kullanıcı için canlı aktivite emit eder.
 * Gönderilenler (mahremiyet dostu): bulunduğu sayfa (pathname), aktif/boşta,
 * sekme odak durumu ve tıklanan buton/link ETİKETİ. Tuş vuruşu / imleç / girdi
 * içeriği GÖNDERİLMEZ. Routes'taki <ActivityTrackers /> içinde bir kez mount edilir;
 * chat soket context'ini tükettiği için rota ağacına taşınmamalıdır (gereksiz remount yaratır).
 */
export const useActivityTracker = () => {
  const { trackRoute, trackAction, socket } = useChatSocket();
  const location = useLocation();

  const pathRef = useRef('');
  const idleRef = useRef(false);
  const lastInputRef = useRef(Date.now());
  const lastActionRef = useRef<{ label: string; at: number }>({ label: '', at: 0 });

  // Rota takibi — mount'ta ve her sayfa değişiminde.
  useEffect(() => {
    const path = location.pathname;
    pathRef.current = path;
    trackRoute(path, typeof document !== 'undefined' ? document.title : undefined);
  }, [location.pathname, trackRoute]);

  // Idle / odak / semantik tıklama + reconnect'te yeniden gönderim.
  useEffect(() => {
    if (!socket) return undefined;

    const markActive = () => {
      lastInputRef.current = Date.now();
      if (idleRef.current) {
        idleRef.current = false;
        if (socket.connected) socket.emit('activity:idle', { idle: false });
      }
    };

    let lastMove = 0;
    const onMove = () => {
      const now = Date.now();
      if (now - lastMove < 1000) return; // throttle — mousemove yayınlanmaz, sadece idle tespiti
      lastMove = now;
      markActive();
    };

    const onClick = (e: MouseEvent) => {
      markActive();
      const target = e.target as HTMLElement | null;
      const el = target && target.closest('button, a, [role="button"], .btn, .menu-link');
      if (!el) return;
      let label = (el.getAttribute('aria-label') || el.getAttribute('title') || el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!label) return;
      if (label.length > 60) label = `${label.slice(0, 60)}…`;
      const now = Date.now();
      if (label === lastActionRef.current.label && now - lastActionRef.current.at < 1500) return; // aynı etiketi spamleme
      lastActionRef.current = { label, at: now };
      trackAction({ type: 'click', label });
    };

    const onKey = () => markActive();
    const onScroll = () => markActive();
    const onVisibility = () => {
      if (socket.connected) socket.emit('activity:focus', { focused: !document.hidden });
      if (!document.hidden) markActive();
    };
    const onConnect = () => {
      // Yeniden bağlanınca mevcut durumu tazele.
      trackRoute(pathRef.current, document.title);
      if (socket.connected) socket.emit('activity:focus', { focused: !document.hidden });
    };

    const idleTimer = setInterval(() => {
      if (!idleRef.current && Date.now() - lastInputRef.current > IDLE_MS) {
        idleRef.current = true;
        if (socket.connected) socket.emit('activity:idle', { idle: true });
      }
    }, 15000);

    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    socket.on('connect', onConnect);

    return () => {
      clearInterval(idleTimer);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('visibilitychange', onVisibility);
      socket.off('connect', onConnect);
    };
  }, [socket, trackRoute, trackAction]);
};
